import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { company } from '~/company/Company';
import type { IconTypes } from '~/components/Icon/IconTypes';
import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import { searchStore } from '~/stores/SearchStore';

export const useSearchRecommendations = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const popular = useMemo(
    () =>
      company.match({
        citydrinks: [
          'Beer',
          'Vodka',
          'Alfonso',
          'Red label',
          'Tequila',
          'Wine',
          'Glen',
          'Red horse',
          'Whiskey',
          'Soju ',
          "Jack Daniel's",
          'Old Monk',
          'Red wine',
          'Black label',
        ],
        default: [],
      }) || [],
    [],
  );

  const handleSelect = useCallback(
    (value: string) => {
      if (value?.trim()) {
        searchParams.set('q', value);
      } else {
        searchParams.delete('q');
      }
      setSearchParams(searchParams, { replace: true });

      searchStore.paginationRequest.loadPage(1, value);

      if (popular.includes(value)) {
        analyticsService.track(AnalyticsEvent.CATALOG_SEARCH_IDEA_CLICKED, {
          query: value,
        });
      }
    },
    [popular],
  );

  const data = useMemo(() => {
    return [
      {
        title: company.match({
          citydrinks: t('header:recent'),
          default: t('searchHistory'),
        }),
        action: (
          <button
            className="button _no-padding"
            disabled={searchStore.clearHistory.isLoading}
            onClick={searchStore.clearHistory.run}
          >
            {t('clear')}
          </button>
        ),
        items: searchStore.history,
        icon: 'clock-3.1' as IconTypes,
      },
      {
        title: company.match({
          citydrinks: t('header:trending'),
          default: t('popularSearches'),
        }),
        items: popular,
        icon: 'hot-3.1' as IconTypes,
      },
    ].filter(({ items }) => Boolean(items.length));
  }, [t, popular, searchStore.history]);

  return [data, handleSelect] as const;
};
